import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import DownloadIcon from "@mui/icons-material/Download";
import { BASE_URL } from "../../api/api";
import PaymentMISHook from "./PaymentMISHook";
import * as XLSX from "xlsx";

const PaymentMIS = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [tableData, setTableData] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const { VENDOR_LIST } = PaymentMISHook();
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const fetchPaymentHistory = async () => {
    try {
      const res = await fetch(`${BASE_URL}/GetRawDataMIS`);
      const data = await res.json();

      if (data?.status && Array.isArray(data.data)) {
        const cleaned = data.data.map(({ financeStatus, networkStatus, ...rest }) => rest);
        setTableData(cleaned);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  // Filter data based on vendor and date
  const filteredData = tableData.filter((row) => {
    // Vendor filter
    if (selectedVendor && row.vendorName?.toLowerCase() !== selectedVendor.toLowerCase()) {
      return false;
    }

    // Date filter (use paymentDate or created_Date)
    if (fromDate || toDate) {
      const recordDate = new Date(row.paymentDate || row.created_Date);
      if (fromDate && recordDate < new Date(fromDate)) return false;
      if (toDate && recordDate > new Date(toDate)) return false;
    }

    return true;
  });

  const formatDate = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    if (!isNaN(d)) {
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }
    return val;
  };

  const formatValue = (val) => {
    if (val === "" || val === null || val === undefined) return "-";
    const num = Number(val);
    if (!isNaN(num)) return Number.isInteger(num) ? num : num.toFixed(2);
    return val;
  };

  const handleAdminDownload = () => {
    if (filteredData.length === 0) return;

    const headers = Object.keys(filteredData[0]);
    const exportRows = filteredData.map((row) => {
      const obj = {};
      headers.forEach((key) => {
        obj[key] = row[key] ?? "-";
      });
      return obj;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(wb, ws, "Payment MIS");
    XLSX.writeFile(wb, "PaymentMIS.xlsx");
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  return (
    <div className="container">
      <div className="pt-3 d-flex flex-wrap gap-3 align-items-end">
        <div style={{ minWidth: "250px" }}>
          <label className="mb-1 text-sm font-medium">Vendor Name</label>
          <select
            className="form-select"
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
          >
            <option value="">All Vendors</option>
            {VENDOR_LIST.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 text-sm font-medium">From Date</label>
          <input
            type="date"
            className="form-control"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>

        <div>
          <label className="mb-1 text-sm font-medium">To Date</label>
          <input
            type="date"
            className="form-control"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>

        <div>
          <button
            className="btn btn-primary"
            style={{ backgroundColor: "#7E00D1", border: 0, width: 200 }}
            onClick={handleAdminDownload}
          >
            <DownloadIcon /> Download MIS
          </button>
        </div>
      </div>

      <div className="container mt-4" style={{ overflow: "auto" }}>
        {currentRows.length > 0 ? (
          <Table className="bg-white text-center border-0 network_table mt-3">
            <thead
              style={{ backgroundColor: "#EEF4FF", position: "sticky", top: 0, zIndex: 1 }}
            >
              <tr className="text-dark fw-semibold table_th_border">
                {Object.keys(currentRows[0])
                  .filter((key) => key !== "financeStatus" && key !== "networkStatus")
                  .map((key) => (
                    <th key={key}>{key}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.map((row, idx) => (
                <tr key={idx} className="text-center border-bottom network_td_item">
                  {Object.entries(row)
                    .filter(([key]) => key !== "financeStatus" && key !== "networkStatus")
                    .map(([key, val]) => (
                      <td key={key}>
                        {key.toLowerCase().includes("date") ? formatDate(val) : formatValue(val)}
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="mt-3">No records found.</p>
        )}
      </div>

      {filteredData.length > rowsPerPage && (
        <div className="d-flex justify-content-center mt-3">
          <nav>
            <ul className="pagination">
              {Array.from({ length: totalPages }, (_, i) => (
                <li
                  key={i}
                  className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default PaymentMIS;
